import type { ExerciseRunPayload } from '@/lib/run-queue'
import { signal } from '@/lib/telemetry'

/**
 * Typed signal helpers — one function per row in the TelemetryDeck event
 * schema. Centralising event names + property shapes here keeps the
 * vocabulary greppable: a developer adding a funnel reads this file once
 * to see every signal the app emits.
 *
 * Naming convention: `category.action`, lowercase snake_case for props.
 * Durations are reported in **seconds**, not ms, so the TelemetryDeck UI
 * shows legible numbers.
 *
 * Default payload (platform, app_version, locale, entitlement_status,
 * session_id, trial_days_remaining) is attached by `signal()` — don't
 * repeat those properties here.
 */

// --- Lifecycle ---------------------------------------------------------

export function trackAppLaunched(opts: {
  isFirstSession: boolean
  coldStart: boolean
}): void {
  signal('app.launched', {
    is_first_session: opts.isFirstSession,
    cold_start: opts.coldStart,
  })
}

export function trackAppResumed(): void {
  signal('app.resumed')
}

// --- Onboarding funnel -------------------------------------------------

export function trackOnboardingTermsViewed(): void {
  signal('onboarding.terms_viewed')
}

export function trackOnboardingTermsAccepted(): void {
  signal('onboarding.terms_accepted')
}

export function trackOnboardingBootstrapSucceeded(): void {
  signal('onboarding.bootstrap_succeeded')
}

export function trackOnboardingBootstrapFailed(errorCode: string): void {
  signal('onboarding.bootstrap_failed', { error_code: errorCode })
}

export function trackHomeViewed(entitlementStatus: string): void {
  // entitlement_status is already on the default payload — we still re-send
  // it here as an explicit named property so the funnel UI lets you segment
  // on the value at the moment the home screen rendered (it's also the
  // gating signal for the trial → paywall conversion funnel).
  signal('home.viewed', { entitlement_status: entitlementStatus })
}

// --- Exercise creation funnel ------------------------------------------

export type ExerciseCreateStep =
  | 'topic'
  | 'difficulty'
  | 'items'
  | 'rating_mode'
  | 'name'

export function trackExerciseCreateStarted(): void {
  signal('exercise.create.started')
}

export function trackExerciseCreateStepCompleted(opts: {
  step: ExerciseCreateStep
  stepIndex: number
  stepDurationSeconds: number
}): void {
  signal('exercise.create.step_completed', {
    step: opts.step,
    step_index: opts.stepIndex,
    step_duration_seconds: Math.round(opts.stepDurationSeconds),
  })
}

export function trackExerciseCreateSaved(opts: {
  topicId: number
  difficultyCount: number
  itemCount: number
  itemsCustomized: boolean
  ratingMode: string
  wizardDurationSeconds: number
}): void {
  signal('exercise.create.saved', {
    topic_id: opts.topicId,
    difficulty_count: opts.difficultyCount,
    item_count: opts.itemCount,
    items_customized: opts.itemsCustomized,
    rating_mode: opts.ratingMode,
    wizard_duration_seconds: Math.round(opts.wizardDurationSeconds),
  })
}

export function trackExerciseCreateAbandoned(opts: {
  lastStep: ExerciseCreateStep
  wizardDurationSeconds: number
}): void {
  signal('exercise.create.abandoned', {
    last_step: opts.lastStep,
    wizard_duration_seconds: Math.round(opts.wizardDurationSeconds),
  })
}

export function trackExerciseCreateDuplicateName(): void {
  signal('exercise.create.duplicate_name')
}

// --- Exercise list -----------------------------------------------------

export function trackExerciseListViewed(exerciseCount: number): void {
  signal('exercise.list.viewed', { exercise_count: exerciseCount })
}

export function trackExerciseDeleted(): void {
  signal('exercise.deleted')
}

export function trackExerciseResumed(): void {
  // Fired when the picker hands off to a session that hydrates from a
  // resumable snapshot. TrainingView's mount detects the snapshot and
  // tells us via the `resumed: true` property on `training.started`, so
  // this signal is the picker-side counterpart for funnel symmetry.
  signal('exercise.resumed')
}

// --- Training (run-level only) -----------------------------------------

export type RatingMode = 'speech_recognition' | 'self_assessment' | 'none'

export function trackTrainingStarted(opts: {
  ratingMode: RatingMode
  itemCount: number
  resumed: boolean
}): void {
  signal('training.started', {
    rating_mode: opts.ratingMode,
    item_count: opts.itemCount,
    resumed: opts.resumed,
  })
}

/**
 * Aggregate counts of `helps_used` entries by `kind` across every item in
 * the run. Used to fill the `helps_*_count` fields on `training.completed`
 * without sending per-item events.
 */
function helpsCountByKind(payload: ExerciseRunPayload): {
  mouth: number
  cloud: number
  ear: number
  ab: number
} {
  let mouth = 0
  let cloud = 0
  let ear = 0
  let ab = 0
  for (const item of payload.items) {
    for (const help of item.helps_used) {
      if (help.kind === 'mouth') mouth++
      else if (help.kind === 'cloud') cloud++
      else if (help.kind === 'ear') ear++
      else if (help.kind === 'ab') ab++
    }
  }
  return { mouth, cloud, ear, ab }
}

export function trackTrainingCompleted(payload: ExerciseRunPayload): void {
  const total = payload.items.length
  const scorePercent =
    total === 0 ? 0 : Math.round((100 * payload.items_correct) / total)
  const helps = helpsCountByKind(payload)
  signal('training.completed', {
    rating_mode: payload.rating_mode,
    item_count: total,
    items_correct: payload.items_correct,
    items_failed: payload.items_failed,
    duration_seconds: Math.round(payload.duration_ms / 1000),
    score_percent: scorePercent,
    helps_mouth_count: helps.mouth,
    helps_cloud_count: helps.cloud,
    helps_ear_count: helps.ear,
    helps_ab_count: helps.ab,
  })
}

export function trackTrainingAbandoned(opts: {
  ratingMode: RatingMode
  itemCount: number
  itemsCompleted: number
  durationSeconds: number
}): void {
  signal('training.abandoned', {
    rating_mode: opts.ratingMode,
    item_count: opts.itemCount,
    items_completed: opts.itemsCompleted,
    duration_seconds: Math.round(opts.durationSeconds),
  })
}

// --- Results -----------------------------------------------------------

export function trackResultViewed(opts: {
  scorePercent: number
  hasPreviousRun: boolean
}): void {
  signal('result.viewed', {
    score_percent: opts.scorePercent,
    has_previous_run: opts.hasPreviousRun,
  })
}

export function trackResultRepeatTapped(): void {
  signal('result.repeat_tapped')
}

// --- Settings ----------------------------------------------------------

export function trackSettingsViewed(): void {
  signal('settings.viewed')
}

export function trackSettingsChanged(opts: {
  key: string
  value: string | number | boolean
}): void {
  signal('settings.changed', { key: opts.key, value: opts.value })
}

export function trackSettingsSubscriptionTapped(
  route: 'appstore' | 'playstore' | 'paywall_fallback',
): void {
  signal('settings.subscription_tapped', { route })
}

/**
 * Grace is what is granting access on this launch: a subscription past its
 * stamped expiry, honoured anyway.
 *
 * `window` means the expiry was recent; `unseen` means we had not seen this
 * install since it passed. A rising share of either is the signal that renewals
 * are going unobserved for long enough to matter — i.e. the argument for App
 * Store Server Notifications, which we have deliberately not built.
 */
export function trackEntitlementGrace(reason: 'window' | 'unseen'): void {
  signal('entitlement.grace', { reason })
}

/**
 * The outcome of a pre-interruption entitlement re-check. `store` is the
 * interesting one: it counts the times we were about to show a paying
 * subscriber a paywall and caught it. A rising `still_blocked` with a falling
 * `store` means the renewal path is healthy; `unavailable` counts the patients
 * we interrupted without being able to check.
 */
export function trackEntitlementReconciled(opts: {
  outcome: 'server' | 'store' | 'still_blocked' | 'unavailable'
  from: string
}): void {
  signal('entitlement.reconciled', { outcome: opts.outcome, from: opts.from })
}

// --- Device transfer ---------------------------------------------------

export function trackTransferCodeIssued(): void {
  signal('transfer.code_issued')
}

export function trackTransferRedeemed(opts: { exercises: number; runs: number }): void {
  signal('transfer.redeemed', { exercises: opts.exercises, runs: opts.runs })
}

/**
 * Reason buckets only — never the code the user typed. A spike in `invalid` or
 * `rate_limited` across many devices is what a brute-force campaign against the
 * codespace looks like, and that signal does not need the input to be useful.
 */
export function trackTransferRedeemFailed(
  reason: 'invalid' | 'rate_limited' | 'network' | 'server',
): void {
  signal('transfer.redeem_failed', { reason })
}

/** A redemption finished through the claim retry, i.e. the first response was lost. */
export function trackTransferClaimed(): void {
  signal('transfer.claimed')
}

/** This device handed its account over and stood down. */
export function trackTransferRetired(): void {
  signal('transfer.retired')
}

// --- Revenue funnel ----------------------------------------------------

export function trackTrialExpiredDetected(): void {
  signal('trial.expired_detected')
}

export type PaywallSource = 'pricing_link' | 'trial_expired' | 'expired_bar' | 'feature_gate'

export function trackPaywallOpened(source: PaywallSource): void {
  signal('paywall.opened', { source })
}

export function trackPaywallClosed(reason: 'user_close' | 'unmount'): void {
  signal('paywall.closed', { reason })
}

export type PurchaseProduct = 'lifetime' | 'monthly'
export type PurchasePlatform = 'ios' | 'android' | 'web'

/**
 * Per-transaction pricing as quoted by the App Store / Play Store at the
 * moment of purchase. `priceMicros` is integer micros (14990000 = €14.99)
 * so cross-currency math stays precise. `currency` is the ISO 4217 code.
 *
 * Both nullable because the store catalog can take a beat to load; we'd
 * rather emit the funnel event with no revenue than block on a fetch.
 * Subscription renewals fire the same `purchase.completed` signal — each
 * cycle's revenue is one event, so monthly LTV is `count × price_micros`.
 */
export interface PurchasePricing {
  priceMicros: number | null
  currency: string | null
}

function pricingProps(p: PurchasePricing): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (p.priceMicros !== null) out.price_micros = p.priceMicros
  if (p.currency !== null) out.currency = p.currency
  return out
}

export function trackPurchaseStarted(opts: {
  product: PurchaseProduct
  platform: PurchasePlatform
  pricing: PurchasePricing
}): void {
  signal('purchase.started', {
    product: opts.product,
    platform: opts.platform,
    ...pricingProps(opts.pricing),
  })
}

export function trackPurchaseCompleted(opts: {
  product: PurchaseProduct
  platform: PurchasePlatform
  pricing: PurchasePricing
}): void {
  signal('purchase.completed', {
    product: opts.product,
    platform: opts.platform,
    ...pricingProps(opts.pricing),
  })
}

export function trackPurchaseFailed(opts: {
  product: PurchaseProduct
  platform: PurchasePlatform
  pricing: PurchasePricing
  errorCode: string
}): void {
  signal('purchase.failed', {
    product: opts.product,
    platform: opts.platform,
    error_code: opts.errorCode,
    ...pricingProps(opts.pricing),
  })
}

export function trackRestoreStarted(): void {
  signal('restore.started')
}

export function trackRestoreCompleted(productsRestoredCount: number): void {
  signal('restore.completed', {
    products_restored_count: productsRestoredCount,
  })
}

export function trackRestoreFailed(errorCode: string): void {
  signal('restore.failed', { error_code: errorCode })
}

// --- Review prompt -----------------------------------------------------

/**
 * The only production visibility we get into the review funnel. Neither the
 * App Store nor Play reports whether a prompt was displayed or converted, so
 * conversion is measured indirectly: `review.prompt_requested` counts here
 * (per platform — the TelemetryDeck app ids are already split) against new
 * ratings in App Store Connect / Play Console. See lib/review-prompt.ts.
 */
export function trackReviewPromptRequested(opts: {
  /** 1 for the first prompt, 2 for the reminder. */
  window: number
  runIndex: number
  scorePercent: number
}): void {
  signal('review.prompt_requested', {
    window: opts.window,
    run_index: opts.runIndex,
    score_percent: opts.scorePercent,
  })
}

/**
 * Only fired for near-misses (`below-threshold`, `run-cooldown`,
 * `time-cooldown`) — see REPORTABLE_SKIPS. The steady-state skips would
 * otherwise emit one signal per completed exercise for no insight.
 */
export function trackReviewPromptSkipped(opts: {
  reason: string
  runIndex: number
  scorePercent: number
}): void {
  signal('review.prompt_skipped', {
    reason: opts.reason,
    run_index: opts.runIndex,
    score_percent: opts.scorePercent,
  })
}
